import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { useRealTimeStatusUpdates } from '../hooks/useRealTimeStatusUpdates';
import { useToast } from './ToastContext';

/**
 * Real-time Status Context for Knowledge Base sync operations
 * 
 * Features:
 * - Centralized real-time status management
 * - Status change animations and visual feedback
 * - Automatic UI updates when sync operations complete
 * - Connection health monitoring and fallback handling
 * 
 * Requirements: 1.4, 2.5, 4.5 - Real-time status updates for Knowledge Base operations
 */

// Action types
const ACTIONS = {
  SET_CONNECTION_STATUS: 'SET_CONNECTION_STATUS',
  UPDATE_STATUS: 'UPDATE_STATUS',
  SET_SUBSCRIPTIONS: 'SET_SUBSCRIPTIONS',
  ADD_ANIMATION: 'ADD_ANIMATION',
  REMOVE_ANIMATION: 'REMOVE_ANIMATION',
  SET_DEGRADED_MODE: 'SET_DEGRADED_MODE',
  UPDATE_PARROT_STATUS: 'UPDATE_PARROT_STATUS',
  UPDATE_FILE_STATUS: 'UPDATE_FILE_STATUS',
  CLEAR_STATUS: 'CLEAR_STATUS'
};

// Initial state
const initialState = {
  // Connection state
  isConnected: false,
  connectionStatus: 'disconnected',
  connectionError: null,
  degradedMode: null,

  // Subscriptions
  subscriptions: {
    parrotIds: [],
    fileIds: [],
    sourceTypes: []
  },

  // Status data
  overallStatus: null,
  parrotStatuses: new Map(),
  fileStatuses: new Map(),
  lastUpdate: null,

  // UI state
  activeAnimations: new Map(),
  pendingUpdates: new Set(),
  
  // Statistics
  updateCount: 0,
  connectionUptime: null
};

// Reducer
function realTimeStatusReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_CONNECTION_STATUS:
      return {
        ...state,
        isConnected: action.payload.isConnected,
        connectionStatus: action.payload.status,
        connectionError: action.payload.error || null,
        connectionUptime: action.payload.isConnected ? Date.now() : null
      };

    case ACTIONS.UPDATE_STATUS:
      return {
        ...state,
        lastUpdate: action.payload,
        updateCount: state.updateCount + 1
      };

    case ACTIONS.SET_SUBSCRIPTIONS:
      return {
        ...state,
        subscriptions: action.payload
      };

    case ACTIONS.ADD_ANIMATION:
      const newAnimations = new Map(state.activeAnimations);
      newAnimations.set(action.payload.key, {
        type: action.payload.type,
        data: action.payload.data,
        startTime: Date.now()
      });
      return {
        ...state,
        activeAnimations: newAnimations
      };

    case ACTIONS.REMOVE_ANIMATION:
      const updatedAnimations = new Map(state.activeAnimations);
      updatedAnimations.delete(action.payload.key);
      return {
        ...state,
        activeAnimations: updatedAnimations
      };

    case ACTIONS.SET_DEGRADED_MODE:
      return {
        ...state,
        degradedMode: action.payload
      };

    case ACTIONS.UPDATE_PARROT_STATUS:
      const updatedParrotStatuses = new Map(state.parrotStatuses);
      updatedParrotStatuses.set(action.payload.parrotId, {
        ...action.payload.status,
        lastUpdated: Date.now()
      });
      return {
        ...state,
        parrotStatuses: updatedParrotStatuses
      };

    case ACTIONS.UPDATE_FILE_STATUS:
      const updatedFileStatuses = new Map(state.fileStatuses);
      updatedFileStatuses.set(action.payload.fileId, {
        ...action.payload.status,
        lastUpdated: Date.now()
      });
      return {
        ...state,
        fileStatuses: updatedFileStatuses
      };

    case ACTIONS.CLEAR_STATUS:
      return {
        ...state,
        overallStatus: null,
        parrotStatuses: new Map(),
        fileStatuses: new Map(),
        activeAnimations: new Map(),
        pendingUpdates: new Set()
      };

    default:
      return state;
  }
}

// Context
const RealTimeStatusContext = createContext();

// Provider component
export const RealTimeStatusProvider = ({ children, initialSubscriptions = {} }) => {
  const [state, dispatch] = useReducer(realTimeStatusReducer, {
    ...initialState,
    subscriptions: { ...initialState.subscriptions, ...initialSubscriptions }
  });
  
  // Use ref to store current state for stable callbacks
  const stateRef = useRef(state);
  stateRef.current = state;
  
  const { showSuccess, showError, showInfo } = useToast();

  // Handle status updates from SSE
  const handleStatusUpdate = useCallback((data, eventType) => {
    dispatch({ type: ACTIONS.UPDATE_STATUS, payload: data });

    switch (eventType) {
      case 'connected':
        showInfo('Real-time status updates connected');
        break;

      case 'initial_status':
        // Process initial status data
        if (data.statuses && Array.isArray(data.statuses)) {
          data.statuses.forEach(status => {
            dispatch({
              type: ACTIONS.UPDATE_PARROT_STATUS,
              payload: {
                parrotId: status.parrot_id,
                status: status
              }
            });

            if (status.source_type === 'file') {
              dispatch({
                type: ACTIONS.UPDATE_FILE_STATUS,
                payload: {
                  fileId: status.source_id,
                  status: status
                }
              });
            }
          });
        }
        break;

      case 'status_update':
        // Handle individual status updates
        const animationKey = `${data.parrot_id}-${data.source_id}`;
        
        // Add status change animation
        dispatch({
          type: ACTIONS.ADD_ANIMATION,
          payload: {
            key: animationKey,
            type: 'status_change',
            data: {
              parrotId: data.parrot_id,
              sourceId: data.source_id,
              oldStatus: state.parrotStatuses.get(data.parrot_id)?.status,
              newStatus: data.status
            }
          }
        });

        // Update parrot status
        dispatch({
          type: ACTIONS.UPDATE_PARROT_STATUS,
          payload: {
            parrotId: data.parrot_id,
            status: data
          }
        });

        // Update file status if applicable
        if (data.source_type === 'file') {
          dispatch({
            type: ACTIONS.UPDATE_FILE_STATUS,
            payload: {
              fileId: data.source_id,
              status: data
            }
          });
        }

        // Remove animation after delay
        setTimeout(() => {
          dispatch({
            type: ACTIONS.REMOVE_ANIMATION,
            payload: { key: animationKey }
          });
        }, 3000);

        // Show notification for important status changes
        if (data.status === 'completed') {
          showSuccess(`${data.source_name} sync completed for ${data.parrot_name}`);
        } else if (data.status === 'failed') {
          showError(`${data.source_name} sync failed for ${data.parrot_name}`);
        }
        break;

      case 'sync_started':
        showInfo(`Starting sync: ${data.source_name} for ${data.parrot_name}`);
        
        // Add sync started animation
        dispatch({
          type: ACTIONS.ADD_ANIMATION,
          payload: {
            key: `sync_start_${data.parrot_id}_${data.source_id}`,
            type: 'sync_started',
            data: data
          }
        });
        break;

      case 'sync_completed':
        const message = data.success 
          ? `Sync completed: ${data.source_name} for ${data.parrot_name}`
          : `Sync failed: ${data.source_name} for ${data.parrot_name}`;
        
        if (data.success) {
          showSuccess(message);
        } else {
          showError(message);
        }

        // Add completion animation
        dispatch({
          type: ACTIONS.ADD_ANIMATION,
          payload: {
            key: `sync_complete_${data.parrot_id}_${data.source_id}`,
            type: 'sync_completed',
            data: data
          }
        });
        break;

      case 'heartbeat':
        // Silent heartbeat - just update connection health
        break;

      default:
        console.log('Unhandled status update event:', eventType, data);
    }
  }, [showSuccess, showError, showInfo]); // Removed state.parrotStatuses from dependencies to prevent infinite loop

  // Handle connection changes
  const handleConnectionChange = useCallback((status, error = null) => {
    dispatch({
      type: ACTIONS.SET_CONNECTION_STATUS,
      payload: {
        isConnected: status === 'connected',
        status,
        error
      }
    });

    // Handle degraded mode
    if (status === 'error' && error) {
      dispatch({
        type: ACTIONS.SET_DEGRADED_MODE,
        payload: {
          enabled: true,
          reason: 'connection_failed',
          strategy: 'polling',
          pollingInterval: 30000,
          message: 'Real-time updates unavailable. Using automatic refresh.',
          enabledAt: new Date().toISOString()
        }
      });
    } else if (status === 'connected') {
      dispatch({
        type: ACTIONS.SET_DEGRADED_MODE,
        payload: null
      });
    }
  }, []);

  // Handle connection errors
  const handleConnectionError = useCallback((error) => {
    console.error('Real-time status connection error:', error);
    showError(`Connection error: ${error.message}`);
  }, [showError]);

  // Set up real-time connection
  const {
    isConnected,
    connectionStatus,
    connectionError,
    connect,
    disconnect,
    reconnect,
    updateSubscriptions: updateSSESubscriptions
  } = useRealTimeStatusUpdates({
    subscriptions: state.subscriptions,
    autoConnect: false, // Temporarily disabled due to CORS issues
    onStatusUpdate: handleStatusUpdate,
    onConnectionChange: handleConnectionChange,
    onError: handleConnectionError
  });

  // Update connection status in state
  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_CONNECTION_STATUS,
      payload: {
        isConnected,
        status: connectionStatus,
        error: connectionError
      }
    });
  }, [isConnected, connectionStatus, connectionError]); // Note: Keeping all dependencies as they are needed for the effect to work correctly

  // Context value
  const contextValue = {
    // State
    ...state,
    
    // Connection controls
    connect,
    disconnect,
    reconnect,
    
    // Subscription management
    updateSubscriptions: useCallback((newSubscriptions) => {
      dispatch({ type: ACTIONS.SET_SUBSCRIPTIONS, payload: newSubscriptions });
      updateSSESubscriptions(newSubscriptions);
    }, [updateSSESubscriptions]), // Note: Keeping all dependencies as they are needed for the function to work correctly

    subscribeToParrot: useCallback((parrotId) => {
      const currentSubscriptions = stateRef.current.subscriptions;
      const newSubscriptions = {
        ...currentSubscriptions,
        parrotIds: [...new Set([...currentSubscriptions.parrotIds, parrotId])]
      };
      dispatch({ type: ACTIONS.SET_SUBSCRIPTIONS, payload: newSubscriptions });
      updateSSESubscriptions(newSubscriptions);
    }, [updateSSESubscriptions]),

    unsubscribeFromParrot: useCallback((parrotId) => {
      const currentSubscriptions = stateRef.current.subscriptions;
      const newSubscriptions = {
        ...currentSubscriptions,
        parrotIds: currentSubscriptions.parrotIds.filter(id => id !== parrotId)
      };
      dispatch({ type: ACTIONS.SET_SUBSCRIPTIONS, payload: newSubscriptions });
      updateSSESubscriptions(newSubscriptions);
    }, [updateSSESubscriptions]),

    subscribeToFile: useCallback((fileId) => {
      // Use ref to get current state without causing dependency changes
      const currentSubscriptions = stateRef.current.subscriptions;
      const newSubscriptions = {
        ...currentSubscriptions,
        fileIds: [...new Set([...currentSubscriptions.fileIds, fileId])]
      };
      dispatch({ type: ACTIONS.SET_SUBSCRIPTIONS, payload: newSubscriptions });
      updateSSESubscriptions(newSubscriptions);
    }, [updateSSESubscriptions]),

    unsubscribeFromFile: useCallback((fileId) => {
      // Use ref to get current state without causing dependency changes
      const currentSubscriptions = stateRef.current.subscriptions;
      const newSubscriptions = {
        ...currentSubscriptions,
        fileIds: currentSubscriptions.fileIds.filter(id => id !== fileId)
      };
      dispatch({ type: ACTIONS.SET_SUBSCRIPTIONS, payload: newSubscriptions });
      updateSSESubscriptions(newSubscriptions);
    }, [updateSSESubscriptions]),

    // Status queries
    getParrotStatus: useCallback((parrotId) => {
      return stateRef.current.parrotStatuses.get(parrotId);
    }, []),

    getFileStatus: useCallback((fileId) => {
      return stateRef.current.fileStatuses.get(fileId);
    }, []),

    // Animation controls
    addAnimation: useCallback((key, type, data) => {
      dispatch({
        type: ACTIONS.ADD_ANIMATION,
        payload: { key, type, data }
      });
    }, []),

    removeAnimation: useCallback((key) => {
      dispatch({
        type: ACTIONS.REMOVE_ANIMATION,
        payload: { key }
      });
    }, []),

    // Utility functions
    clearAllStatus: useCallback(() => {
      dispatch({ type: ACTIONS.CLEAR_STATUS });
    }, []),

    getConnectionUptime: useCallback(() => {
      if (!stateRef.current.connectionUptime) return null;
      return Date.now() - stateRef.current.connectionUptime;
    }, [])
  };

  return (
    <RealTimeStatusContext.Provider value={contextValue}>
      {children}
    </RealTimeStatusContext.Provider>
  );
};

// Hook to use the context
export const useRealTimeStatus = () => {
  const context = useContext(RealTimeStatusContext);
  if (!context) {
    throw new Error('useRealTimeStatus must be used within a RealTimeStatusProvider');
  }
  return context;
};

export default RealTimeStatusContext;
